/**
 * Self-contained multi-select dropdown with search for mobile filter sheets.
 *
 * Manages its own open/close and search state. Supports two search modes:
 * - **Client-side** (default): Filters options locally via case-insensitive match.
 * - **Server-side** (when `onExternalSearchChange` is provided): Delegates search
 *   to the parent via callback; options are pre-filtered by the server.
 * @module components/shared/MobileFilterSheet/MobileMultiSelect/MobileMultiSelectContainer
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";
import { useDebouncedCallback } from "use-debounce";

import MobileMultiSelectView from "./MobileMultiSelectView";

import type { FilterOption } from "@/types/filter";

/** Props for MobileMultiSelectContainer */
interface MobileMultiSelectContainerProps {
  /** Label shown above the dropdown trigger (e.g. "Positions") */
  readonly label: string;
  /** Placeholder text when no items are selected */
  readonly placeholder: string;
  /** Available options to choose from */
  readonly options: readonly FilterOption[];
  /** Currently selected option IDs */
  readonly selectedIds: readonly string[];
  /** Toggle a single option on/off */
  readonly onToggle: (id: string) => void;
  /** Clear all selected options */
  readonly onClear: () => void;
  /** Whether dark mode is active — for portal-safe styling */
  readonly isDark: boolean;
  /** External search text (server-side search mode) */
  readonly externalSearchText?: string;
  /** Called when search text changes (enables server-side search mode) */
  readonly onExternalSearchChange?: (text: string) => void;
  /** Whether a server-side search is in progress */
  readonly isSearching?: boolean;
}

/**
 * Container for the mobile multi-select dropdown.
 * Owns search text and open/close state; derives filtered options.
 * @param root0 - Component props
 * @param root0.label - Dropdown label
 * @param root0.placeholder - Placeholder text
 * @param root0.options - Available options
 * @param root0.selectedIds - Selected option IDs
 * @param root0.onToggle - Toggle callback
 * @param root0.onClear - Clear all callback
 * @param root0.isDark - Whether dark mode is active
 * @param root0.externalSearchText - External search text for server mode
 * @param root0.onExternalSearchChange - Server search callback
 * @param root0.isSearching - Whether server search is loading
 */
const MobileMultiSelectContainer = ({
  label,
  placeholder,
  options,
  selectedIds,
  onToggle,
  onClear,
  isDark,
  externalSearchText: _externalSearchText,
  onExternalSearchChange,
  isSearching = false,
}: MobileMultiSelectContainerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchText, setLocalSearchText] = useState("");
  const rootRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLElement | null>(null);

  const isServerSearch = onExternalSearchChange !== undefined;

  /** Debounce server-side search to prevent rapid re-queries (matches web's 300ms) */
  const debouncedServerSearch = useDebouncedCallback(
    (text: string) => onExternalSearchChange?.(text),
    300
  );

  /** Filter locally only in client-search mode; server-search results are already filtered */
  const filteredOptions = useMemo(() => {
    if (isServerSearch) return options;
    return localSearchText.length === 0
      ? options
      : options.filter(o =>
          o.label.toLowerCase().includes(localSearchText.toLowerCase())
        );
  }, [options, localSearchText, isServerSearch]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setLocalSearchText(text);
      if (isServerSearch) {
        debouncedServerSearch(text);
      }
    },
    [isServerSearch, debouncedServerSearch]
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchText("");
    debouncedServerSearch.cancel();
    if (isServerSearch) {
      onExternalSearchChange("");
    }
  }, [isServerSearch, onExternalSearchChange, debouncedServerSearch]);

  const handleToggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    if (isOpen) {
      setLocalSearchText("");
      debouncedServerSearch.cancel();
      if (isServerSearch) {
        onExternalSearchChange("");
      }
    }
  }, [isOpen, isServerSearch, onExternalSearchChange, debouncedServerSearch]);

  const handleClose = useCallback((shouldFocusToggle = false) => {
    setIsOpen(false);
    if (shouldFocusToggle) {
      toggleRef.current?.focus?.();
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!rootRef.current?.contains(target)) {
        handleClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose(true);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClose, isOpen]);

  return (
    <MobileMultiSelectView
      label={label}
      placeholder={placeholder}
      options={filteredOptions}
      selectedIds={selectedIds}
      selectedCount={selectedIds.length}
      isOpen={isOpen}
      rootRef={rootRef}
      toggleRef={toggleRef}
      isDark={isDark}
      searchText={localSearchText}
      isSearching={isSearching}
      onToggleOpen={handleToggleOpen}
      onSearchChange={handleSearchChange}
      onClearSearch={handleClearSearch}
      onToggle={onToggle}
      onClear={onClear}
    />
  );
};

MobileMultiSelectContainer.displayName = "MobileMultiSelectContainer";

export default memo(MobileMultiSelectContainer);
